/**
 * News state lives in the browser: which updates a visitor has already seen is not
 * worth a database write, and keeping it client-side means the bell also works for
 * anonymous visitors. Pure helpers only, so they can be unit tested.
 */

export const NEWS_STORAGE_KEY = 'silqai.news.lastSeenAt'

/** How many entries the header dropdown shows. The unread dot considers the same set. */
export const NEWS_DROPDOWN_LIMIT = 5

export interface NewsItem {
    slug: string
    title: string
    publishedAt: string
}

/** JSON array of slugs the visitor has opened, the way Auphonic keeps a per-entry read flag. */
export const NEWS_READ_STORAGE_KEY = 'silqai.news.readSlugs'

/**
 * `baseline` is when the visitor first arrived: older entries are not news to them. A
 * first-time visitor has no baseline and sees nothing as unread. An entry stays unread until
 * it is opened — merely opening the bell does not mark anything as read.
 */
export function isUnread(item: NewsItem, baseline: string | null, readSlugs: readonly string[] = []): boolean {
    if (!baseline) return false
    if (readSlugs.includes(item.slug)) return false
    return item.publishedAt > baseline
}

export function hasUnread(items: NewsItem[], baseline: string | null, readSlugs: readonly string[] = []): boolean {
    return items.some(item => isUnread(item, baseline, readSlugs))
}

/** Groups entries by publication year, newest year first, for the backlog page. */
export function groupByYear(items: NewsItem[]): Array<{ year: number; items: NewsItem[] }> {
    const groups = new Map<number, NewsItem[]>()

    for (const item of items) {
        const year = new Date(item.publishedAt).getUTCFullYear()
        const bucket = groups.get(year)
        if (bucket) bucket.push(item)
        else groups.set(year, [item])
    }

    return [...groups.entries()]
        .sort(([a], [b]) => b - a)
        .map(([year, grouped]) => ({ year, items: grouped }))
}
